const S3_MEDIA_HOST = 'https://scw-commerce-media.s3.amazonaws.com';
const STOREFRONT_HOSTS = new Set(['getscw.com', 'www.getscw.com']);
const MEDIA_PATH_PATTERN = /^\/(?:images|pub\/media|media|downloads)(?:\/|$)/;

/**
 * Keeps storefront-owned media away from the Next image optimizer's own host.
 * Source-faithful URLs belong in data-src; browser-facing src values use S3.
 */
export function getRuntimeMediaUrl(source: string): string {
  if (source.startsWith('/') && MEDIA_PATH_PATTERN.test(source)) {
    return `${S3_MEDIA_HOST}${source}`;
  }

  let url: URL;
  try {
    url = new URL(source);
  } catch {
    return source;
  }

  if (!STOREFRONT_HOSTS.has(url.hostname) || !MEDIA_PATH_PATTERN.test(url.pathname)) {
    return source;
  }

  return `${S3_MEDIA_HOST}${url.pathname}${url.search}${url.hash}`;
}

/**
 * Routes browser-facing media attributes in stored HTML without changing
 * navigational links or source-faithful data attributes.
 */
export function routeStorefrontMediaInHtml(html: string): string {
  const withUrlAttributes = html.replace(
    /\b(src|href|poster)=(["'])([^"']*)\2/gi,
    (_match, attribute: string, quote: string, source: string) =>
      `${attribute}=${quote}${getRuntimeMediaUrl(source)}${quote}`,
  );

  const withSrcsets = withUrlAttributes.replace(
    /\bsrcset=(["'])([^"']*)\1/gi,
    (_match, quote: string, value: string) => {
      const routedValue = value
        .split(',')
        .map((candidate) =>
          candidate.replace(
            /^(\s*)(\S+)(.*)$/u,
            (_candidate, leading: string, source: string, descriptor: string) =>
              `${leading}${getRuntimeMediaUrl(source)}${descriptor}`,
          ),
        )
        .join(',');
      return `srcset=${quote}${routedValue}${quote}`;
    },
  );

  return withSrcsets.replace(
    /url\((["']?)([^"')]+)\1\)/gi,
    (_match, quote: string, source: string) =>
      `url(${quote}${getRuntimeMediaUrl(source.trim())}${quote})`,
  );
}
